import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { openZeffy } from "@/lib/zeffy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const HERO_IMAGE = "/hero/donate-feature.png";

export default function DonationSection({ scrollAnchorId = "donate" }) {
  const [presets, setPresets] = useState([]);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one_time");

  useEffect(() => {
    api
      .get("/donations/presets")
      .then((r) => setPresets(r.data))
      .catch(() => toast.error("Could not load donation options"));
  }, []);

  const goToZeffy = (opts) => {
    openZeffy({ frequency, ...opts });
  };

  const handlePresetClick = (p) => {
    goToZeffy({ amount: p.amount, source: `preset_${p.id}` });
  };

  const handleCustomDonate = () => {
    const amt = custom ? parseFloat(custom) : null;
    if (!amt || isNaN(amt) || amt < 1) {
      toast.error("Please enter a valid amount.");
      return;
    }
    goToZeffy({ amount: amt, source: "custom" });
  };

  const isMonthly = frequency === "monthly";

  return (
    <section
      id={scrollAnchorId}
      data-testid="donation-section"
      className="relative py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left column — story */}
          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.05] tracking-tight text-brand-navy">
                Every gift{" "}
                <span className="text-brand-coral">directly funds</span> a
                child&apos;s afternoon.
              </h2>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                No overhead bloat. No guessing. We show you exactly where your
                donation goes — because parents and funders deserve to see the
                impact of each dollar.
              </p>

              <div className="mt-8 relative rounded-3xl overflow-hidden aspect-[4/3]">
                <img
                  src={HERO_IMAGE}
                  alt="Students in a structured study environment"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="text-xs uppercase tracking-wider font-bold opacity-80">
                    Rancho Cucamonga, CA
                  </div>
                  <div className="font-display font-black text-xl mt-1">
                    10950 Arrow Route — where kids find their footing.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column — donation card */}
          <div className="lg:col-span-7">
            <div className="bg-gradient-to-br from-brand-navy to-slate-900 text-white rounded-3xl p-8 md:p-10 shadow-2xl">
              <div className="flex items-center gap-2 text-brand-yellow text-xs font-bold uppercase tracking-widest mb-3">
                <Heart className="w-4 h-4 fill-brand-yellow" />
                Make an impact today
              </div>
              <h3 className="font-display font-black text-3xl md:text-4xl leading-tight">
                Choose an amount. Fund a real outcome.
              </h3>

              {/* Frequency toggle */}
              <div
                role="tablist"
                aria-label="Donation frequency"
                className="mt-6 inline-flex items-center bg-white/[0.06] border border-white/10 rounded-full p-1 gap-1"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isMonthly}
                  data-testid="donation-frequency-once"
                  onClick={() => setFrequency("one_time")}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
                    !isMonthly
                      ? "bg-white text-brand-navy shadow"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  Give once
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isMonthly}
                  data-testid="donation-frequency-monthly"
                  onClick={() => setFrequency("monthly")}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-all inline-flex items-center gap-2 ${
                    isMonthly
                      ? "bg-brand-coral text-white shadow"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  Give monthly
                  <span
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      isMonthly
                        ? "bg-white/25"
                        : "bg-brand-yellow/30 text-brand-yellow"
                    }`}
                  >
                    3× impact
                  </span>
                </button>
              </div>

              {isMonthly && (
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  Monthly sustainers fund a kid&apos;s spot{" "}
                  <span className="text-brand-yellow font-semibold">
                    every single month
                  </span>{" "}
                  — and unlock long-term impact. Cancel anytime.
                </p>
              )}

              {/* Preset cards — each is a direct Zeffy link */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {presets.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`donation-preset-${p.id}`}
                    onClick={() => handlePresetClick(p)}
                    className="text-left rounded-2xl p-5 border-2 border-white/10 hover:border-brand-coral hover:bg-brand-coral/10 transition-all group"
                  >
                    <div className="flex items-baseline gap-1">
                      <span className="font-display font-black text-3xl">
                        ${p.amount.toFixed(0)}
                      </span>
                      {isMonthly && (
                        <span className="text-sm font-bold text-brand-yellow">
                          /mo
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-300 mt-1 leading-snug">
                      {p.impact}
                      {isMonthly && " — every month."}
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-brand-coral opacity-0 group-hover:opacity-100 transition-opacity">
                      Donate <ExternalLink className="w-3 h-3" />
                    </div>
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div className="mt-6">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                  Or enter a custom amount
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display font-black text-xl text-slate-400">
                      $
                    </span>
                    <Input
                      data-testid="donation-custom-input"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="50"
                      value={custom}
                      onChange={(e) => setCustom(e.target.value)}
                      className="pl-10 h-14 text-lg rounded-xl bg-white/[0.05] border-white/10 text-white placeholder:text-slate-500 focus:border-brand-coral"
                    />
                  </div>
                  <Button
                    data-testid="donation-submit-btn"
                    onClick={handleCustomDonate}
                    className="h-14 px-8 rounded-xl bg-brand-coral hover:bg-brand-coralDark text-white font-bold text-base shadow-xl shadow-orange-600/30 hover:-translate-y-0.5 transition-all"
                  >
                    <Heart className="w-5 h-5 mr-2 fill-white" />
                    {isMonthly ? "Start monthly" : "Donate now"}
                  </Button>
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  Secure checkout powered by Zeffy ·{" "}
                  {isMonthly
                    ? "Monthly recurring · cancel anytime"
                    : "One-time donation"}{" "}
                  · Tax-deductible · 100% of your gift reaches the program.
                </p>
              </div>
            </div>

            {/* Tiny trust row */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-6 grid grid-cols-3 gap-3 text-center"
            >
              {[
                { stat: "$25", note: "1 week of snacks" },
                { stat: "$100", note: "1 month of access" },
                { stat: "$500", note: "1 full pillar funded" },
              ].map((t, i) => (
                <div
                  key={i}
                  className="bg-brand-sand rounded-2xl p-4 border border-slate-200"
                >
                  <div className="font-display font-black text-2xl text-brand-navy">
                    {t.stat}
                  </div>
                  <div className="text-xs text-slate-600 font-medium mt-1">
                    {t.note}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
